import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { MonthlyData, BusinessModelType, FinancialModelSummary } from '@/types/financial-model';
import { formatCurrency, formatPercent } from '@/utils/financial-calculations';

interface FinancialModelTableProps {
  data: MonthlyData[];
  modelType: BusinessModelType;
  summary: FinancialModelSummary;
}

// ---- Metric row definition ----

interface MetricRow {
  key: string;
  label: string;
  getValue: (m: MonthlyData) => number | null;
  getSummaryValue: (data: MonthlyData[], summary: FinancialModelSummary) => number | null;
  format: 'currency' | 'number' | 'percent' | 'ratio';
  colorize?: boolean;         // Apply positive/negative coloring
  showForModels?: BusinessModelType[];  // If specified, only show for these models
  tooltip?: string;           // Tooltip text shown on hover
}

// ---- Section definition ----

interface MetricSection {
  title: string;
  rows: MetricRow[];
}

function formatValue(value: number | null, format: MetricRow['format']): string {
  if (value === null) return '\u2014';
  switch (format) {
    case 'currency':
      return formatCurrency(value);
    case 'number':
      return formatCurrency(value); // same thousand-separator formatting
    case 'percent':
      return formatPercent(value);
    case 'ratio':
      return value.toFixed(2);
    default:
      return String(value);
  }
}

function sumField(data: MonthlyData[], fn: (m: MonthlyData) => number | null): number | null {
  const values = data.map(fn).filter((v): v is number => v !== null);
  if (values.length === 0) return null;
  return values.reduce((a, b) => a + b, 0);
}

function avgField(data: MonthlyData[], fn: (m: MonthlyData) => number | null): number | null {
  const values = data.map(fn).filter((v): v is number => v !== null);
  if (values.length === 0) return null;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

function lastField(data: MonthlyData[], fn: (m: MonthlyData) => number | null): number | null {
  const last = data[data.length - 1];
  if (!last) return null;
  return fn(last);
}

function buildSections(t: TFunction): MetricSection[] {
  return [
    {
      title: t('table.section.revenue'),
      rows: [
        {
          key: 'revenue',
          label: t('table.row.revenue'),
          getValue: (m) => m.revenue,
          getSummaryValue: (_data, summary) => summary.totalRevenue,
          format: 'currency',
        },
      ],
    },
    {
      title: t('table.section.customers'),
      rows: [
        {
          key: 'newCustomers',
          label: t('table.row.newCustomers'),
          getValue: (m) => m.newCustomers,
          getSummaryValue: (data) => sumField(data, (m) => m.newCustomers),
          format: 'number',
        },
        {
          key: 'totalCustomers',
          label: t('table.row.totalCustomers'),
          getValue: (m) => m.totalCustomers,
          getSummaryValue: (data) => lastField(data, (m) => m.totalCustomers),
          format: 'number',
        },
        {
          key: 'churnedCustomers',
          label: t('table.row.churn'),
          getValue: (m) => m.churnedCustomers,
          getSummaryValue: (data) => sumField(data, (m) => m.churnedCustomers),
          format: 'number',
          showForModels: ['saas'],
        },
      ],
    },
    {
      title: t('table.section.expenses'),
      rows: [
        {
          key: 'advertisingCost',
          label: t('table.row.advertising'),
          getValue: (m) => m.advertisingCost,
          getSummaryValue: (data) => sumField(data, (m) => m.advertisingCost),
          format: 'currency',
        },
        {
          key: 'variableCosts',
          label: t('table.row.variableCosts'),
          getValue: (m) => m.variableCosts,
          getSummaryValue: (data) => sumField(data, (m) => m.variableCosts),
          format: 'currency',
        },
        {
          key: 'infrastructureCost',
          label: t('table.row.infrastructure'),
          getValue: (m) => m.infrastructureCost,
          getSummaryValue: (data) => sumField(data, (m) => m.infrastructureCost),
          format: 'currency',
        },
        {
          key: 'paymentProcessingCost',
          label: t('table.row.paymentProcessing'),
          getValue: (m) => m.paymentProcessingCost,
          getSummaryValue: (data) => sumField(data, (m) => m.paymentProcessingCost),
          format: 'currency',
        },
        {
          key: 'totalExpenses',
          label: t('table.row.totalExpenses'),
          getValue: (m) => m.totalExpenses,
          getSummaryValue: (_data, summary) => summary.totalExpenses,
          format: 'currency',
        },
      ],
    },
    {
      title: t('table.section.profit'),
      rows: [
        {
          key: 'ebitda',
          label: 'EBITDA',
          getValue: (m) => m.ebitda,
          getSummaryValue: (data) => sumField(data, (m) => m.ebitda),
          format: 'currency',
          colorize: true,
        },
        {
          key: 'taxes',
          label: t('table.row.taxes'),
          getValue: (m) => m.taxes,
          getSummaryValue: (data) => sumField(data, (m) => m.taxes),
          format: 'currency',
        },
        {
          key: 'netProfit',
          label: t('table.row.netProfit'),
          getValue: (m) => m.netProfit,
          getSummaryValue: (_data, summary) => summary.totalNetProfit,
          format: 'currency',
          colorize: true,
        },
        {
          key: 'cashBalance',
          label: t('table.row.cashBalance'),
          getValue: (m) => m.cashBalance,
          getSummaryValue: (_data, summary) => summary.finalCashBalance,
          format: 'currency',
          colorize: true,
        },
      ],
    },
    {
      title: t('table.section.unitEconomics'),
      rows: [
        {
          key: 'cac',
          label: 'CAC',
          getValue: (m) => m.cac,
          getSummaryValue: (_data, summary) => summary.avgCac,
          format: 'currency',
          tooltip: t('table.tooltip.cac'),
        },
        {
          key: 'arpc',
          label: 'ARPC',
          getValue: (m) => m.arpc,
          getSummaryValue: (data) => avgField(data, (m) => m.arpc),
          format: 'currency',
          tooltip: t('table.tooltip.arpc'),
        },
        {
          key: 'ltv',
          label: 'LTV',
          getValue: (m) => m.ltv,
          getSummaryValue: (_data, summary) => summary.avgLtv,
          format: 'currency',
          showForModels: ['saas'],
          tooltip: t('table.tooltip.ltv'),
        },
        {
          key: 'ltvCacRatio',
          label: 'LTV/CAC',
          getValue: (m) => m.ltvCacRatio,
          getSummaryValue: (_data, summary) => summary.avgLtvCacRatio,
          format: 'ratio',
          showForModels: ['saas'],
          tooltip: t('table.tooltip.ltvCac'),
        },
      ],
    },
  ];
}

function getValueColor(value: number | null, colorize?: boolean): string {
  if (!colorize || value === null) return '';
  if (value > 0) return 'text-[#27ae60]';
  if (value < 0) return 'text-[#e74c3c]';
  return '';
}

export default function FinancialModelTable({
  data,
  modelType,
  summary,
}: FinancialModelTableProps) {
  const { t } = useTranslation('financial-model');
  const MONTH_KEYS = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'] as const;
  const monthNames = MONTH_KEYS.map(k => t(`wizard.months.${k}`));
  const sections = buildSections(t);

  // Filter rows that don't apply to this model type
  const filteredSections = sections
    .map((section) => ({
      ...section,
      rows: section.rows.filter(
        (row) => !row.showForModels || row.showForModels.includes(modelType),
      ),
    }))
    .filter((section) => section.rows.length > 0);

  return (
    <div className="bg-white rounded-[16px] border border-[#eeeeee] overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            {/* Empty corner cell */}
            <th className="sticky left-0 z-10 bg-white min-w-[180px] p-[12px] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]" />
            {data.map((m) => (
              <th
                key={m.month}
                className={cn(
                  "text-[13px] font-manrope font-semibold text-[#565858] text-right p-[12px] min-w-[100px] border-b border-[#f0f0f0]",
                  summary.breakEvenMonth === m.month && 'bg-[rgba(33,151,241,0.05)]',
                )}
              >
                {monthNames[m.month - 1]}
              </th>
            ))}
            <th className="text-[13px] font-manrope font-semibold text-[#565858] text-right p-[12px] min-w-[100px] bg-[#f8f8f8] border-b border-[#f0f0f0]">
              {t('table.total')}
            </th>
          </tr>
        </thead>
        <tbody>
          {filteredSections.map((section) => (
            <SectionBlock
              key={section.title}
              section={section}
              data={data}
              summary={summary}
              breakEvenMonth={summary.breakEvenMonth}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---- Section block (header + metric rows) ----

function SectionBlock({
  section,
  data,
  summary,
  breakEvenMonth,
}: {
  section: MetricSection;
  data: MonthlyData[];
  summary: FinancialModelSummary;
  breakEvenMonth: number | null;
}) {
  return (
    <>
      {/* Section header */}
      <tr>
        <td
          className="sticky left-0 z-10 bg-[#f8f8f8] font-manrope font-semibold text-[13px] text-[#565858] uppercase tracking-wider p-[10px_12px] min-w-[180px] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]"
        >
          {section.title}
        </td>
        {data.map((m) => (
          <td key={m.month} className="bg-[#f8f8f8] p-[10px_12px]" />
        ))}
        <td className="bg-[#f8f8f8] p-[10px_12px]" />
      </tr>
      {/* Metric rows */}
      {section.rows.map((row) => (
        <MetricRowComponent
          key={row.key}
          row={row}
          data={data}
          summary={summary}
          breakEvenMonth={breakEvenMonth}
        />
      ))}
    </>
  );
}

// ---- Single metric row ----

function MetricRowComponent({
  row,
  data,
  summary,
  breakEvenMonth,
}: {
  row: MetricRow;
  data: MonthlyData[];
  summary: FinancialModelSummary;
  breakEvenMonth: number | null;
}) {
  const summaryValue = row.getSummaryValue(data, summary);

  return (
    <tr className="border-b border-[#f0f0f0]">
      {/* Sticky label column */}
      <td className="sticky left-0 z-10 bg-white min-w-[180px] p-[10px_12px] text-[14px] text-[#565858] whitespace-nowrap shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]">
        {row.label}
        {row.tooltip && (
          <span className="ml-[4px] text-[12px] text-[#969696] cursor-help" title={row.tooltip}>ⓘ</span>
        )}
      </td>

      {/* Monthly data cells */}
      {data.map((m) => {
        const value = row.getValue(m);
        const colorClass = getValueColor(value, row.colorize);
        return (
          <td
            key={m.month}
            className={cn(
              'text-[14px] text-[#0d0d0d] text-right tabular-nums min-w-[100px] p-[10px_12px]',
              colorClass,
              breakEvenMonth === m.month && 'bg-[rgba(33,151,241,0.05)]',
            )}
          >
            {formatValue(value, row.format)}
          </td>
        );
      })}

      {/* Summary column */}
      <td
        className={cn(
          'text-[14px] text-[#0d0d0d] text-right tabular-nums min-w-[100px] p-[10px_12px] font-semibold bg-[#f8f8f8]',
          getValueColor(summaryValue, row.colorize),
        )}
      >
        {formatValue(summaryValue, row.format)}
      </td>
    </tr>
  );
}
